if (!customElements.get('nussmart-gift-configurator')) {
  customElements.define(
    'nussmart-gift-configurator',
    class NussmartGiftConfigurator extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.productInfo = this.closest('product-info');
        this.productInfo?.classList.add('nussmart-gift-product');

        this.addButton = this.querySelector('[data-nm-gift-add]');
        this.quantityInput = this.querySelector('[data-nm-gift-quantity]');
        this.message = this.querySelector('[data-nm-gift-message]');
        this.total = this.querySelector('[data-nm-gift-total]');

        this.onAddClick = this.handleAddClick.bind(this);
        this.onChoiceChange = this.handleChoiceChange.bind(this);

        this.addButton?.addEventListener('click', this.onAddClick);
        this.addEventListener('change', this.onChoiceChange);

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (this.dataset.sectionId && data?.sectionId && this.dataset.sectionId !== data.sectionId) return;

            this.configureNativePicker();
            this.updateQuantityLimit();
            this.updateTotal(data?.variant);
            window.requestAnimationFrame(() => this.updateMainImage());
          });
        }

        this.configureNativePicker();
        this.updateQuantityLimit();
        this.updateTotal();
        window.requestAnimationFrame(() => this.updateMainImage());
      }

      disconnectedCallback() {
        this.addButton?.removeEventListener('click', this.onAddClick);
        this.removeEventListener('change', this.onChoiceChange);
        this.variantChangeUnsubscriber?.();
        this.variantChangeUnsubscriber = undefined;
        this.initialized = false;
      }

      configureNativePicker() {
        if (!this.productInfo) return;

        const contentInput = this.productInfo.querySelector('input[data-option-name="Inhalt"]');
        const contentFieldset = contentInput?.closest('fieldset');
        if (contentFieldset) {
          contentFieldset.classList.add('nm-gift-picker--content');
          const legend = contentFieldset.querySelector('.form__label');
          if (legend) legend.textContent = '1. Inhalt wählen';
        }

        const greetingInput = Array.from(
          this.productInfo.querySelectorAll('input[data-option-name="Grußkarte"]')
        ).find((input) => input.value === 'Ohne Grußkarte');

        const greetingFieldset = greetingInput?.closest('fieldset');
        greetingFieldset?.classList.add('nm-gift-native-greeting');

        if (greetingInput && !greetingInput.checked) {
          this.setReady(false);
          greetingInput.click();
          return;
        }

        if (this.dataset.baseLidAvailable !== 'true') {
          this.setReady(false, 'Derzeit sind keine Geschenkdosen verfügbar.');
          return;
        }

        this.setReady(true);
      }

      getSelectedParentVariant() {
        const script = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
        if (!script?.textContent) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('Nussmart gift configurator: selected variant JSON could not be parsed.', error);
          return null;
        }
      }

      getSelectedLabel() {
        return this.querySelector('input[name^="nm_gift_label_"]:checked');
      }

      getSelectedCard() {
        return this.querySelector('input[name^="nm_gift_card_"]:checked');
      }

      handleChoiceChange(event) {
        if (!event.target.matches('.nm-gift-choice__input, [data-nm-gift-quantity]')) return;

        this.clearMessage();

        if (event.target.matches('input[name^="nm_gift_label_"]')) {
          this.updateMainImage(event.target);
        }

        this.updateQuantityLimit();
        this.updateTotal();
      }

      getInventoryLimit(quantityValue, policyValue) {
        const policy = String(policyValue || '').toLowerCase();
        if (policy === 'continue') return null;

        const quantity = Number.parseInt(quantityValue, 10);
        return Number.isFinite(quantity) ? Math.max(0, quantity) : null;
      }

      updateQuantityLimit() {
        if (!this.quantityInput) return null;

        const baseLimit = this.getInventoryLimit(
          this.dataset.baseLidQuantity,
          this.dataset.baseLidInventoryPolicy
        );
        const label = this.getSelectedLabel();
        const card = this.getSelectedCard();
        const baseLidVariantId = Number(this.dataset.baseLidVariantId || 0);
        const limits = [];

        if (baseLimit !== null) limits.push(baseLimit);

        if (label?.value && Number(label.value) !== baseLidVariantId) {
          const labelLimit = this.getInventoryLimit(label.dataset.inventoryQuantity, label.dataset.inventoryPolicy);
          if (labelLimit !== null) limits.push(labelLimit);
        }

        if (card?.value) {
          const cardLimit = this.getInventoryLimit(card.dataset.inventoryQuantity, card.dataset.inventoryPolicy);
          if (cardLimit !== null) limits.push(cardLimit);
        }

        const maxQuantity = limits.length ? Math.min(...limits) : null;

        if (maxQuantity === null) {
          this.quantityInput.removeAttribute('max');
          return null;
        }

        if (maxQuantity <= 0) {
          this.quantityInput.max = '0';
          this.setReady(false, 'Die gewählte Konfiguration ist derzeit nicht verfügbar.');
          return 0;
        }

        this.quantityInput.max = String(maxQuantity);
        const currentQuantity = Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1);
        if (currentQuantity > maxQuantity) this.quantityInput.value = String(maxQuantity);

        return maxQuantity;
      }

      updateTotal(variantFromEvent = null) {
        const parentVariant = variantFromEvent || this.getSelectedParentVariant();
        const card = this.getSelectedCard();
        const quantity = Math.max(1, Number.parseInt(this.quantityInput?.value, 10) || 1);

        const parentCents = Number(parentVariant?.price || 0);
        const cardCents = Number(card?.dataset.priceCents || 0);
        const unitCents = parentCents + cardCents;
        const totalCents = unitCents * quantity;

        if (!parentCents) return;

        const currency = this.dataset.currency || 'EUR';
        const formatter = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency,
        });

        const priceRoot =
          this.productInfo?.querySelector(`#price-${this.dataset.sectionId}`) ||
          this.productInfo?.querySelector('[id^="price-"]');

        priceRoot
          ?.querySelectorAll(
            '.price__regular .price-item--regular, .price__sale .price-item--sale.price-item--last'
          )
          .forEach((element) => {
            element.textContent = formatter.format(unitCents / 100);
          });

        if (this.total) {
          this.total.textContent = formatter.format(totalCents / 100);
        }
      }

      updateMainImage(labelInput = null) {
        const label = labelInput || this.getSelectedLabel();
        const mediaId = label?.dataset.parentMediaId;
        const mediaGallery = this.productInfo?.querySelector('media-gallery');

        if (!mediaId || !mediaGallery?.setActiveMedia) return;
        mediaGallery.setActiveMedia(mediaId, true);
      }

      setReady(ready, message = '') {
        if (!this.addButton) return;

        this.addButton.disabled = !ready;
        this.addButton.toggleAttribute('aria-disabled', !ready);

        if (message) this.showMessage(message, true);
      }

      setLoading(loading) {
        if (!this.addButton) return;

        this.addButton.disabled = loading;
        this.addButton.toggleAttribute('aria-disabled', loading);
        this.addButton.classList.toggle('loading', loading);
        this.addButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      clearMessage() {
        if (!this.message) return;
        this.message.hidden = true;
        this.message.textContent = '';
        this.message.classList.remove('nm-gift-configurator__message--error');
      }

      showMessage(text, isError = false) {
        if (!this.message) return;
        this.message.textContent = text;
        this.message.hidden = false;
        this.message.classList.toggle('nm-gift-configurator__message--error', isError);
      }

      async handleAddClick() {
        this.clearMessage();

        const parentVariant = this.getSelectedParentVariant();
        const label = this.getSelectedLabel();
        const card = this.getSelectedCard();
        const baseLidVariantId = Number(this.dataset.baseLidVariantId || 0);
        const availableLimit = this.updateQuantityLimit();
        const quantity = Math.max(1, Number.parseInt(this.quantityInput?.value, 10) || 1);

        const baseGreeting = Array.from(
          this.productInfo?.querySelectorAll('input[data-option-name="Grußkarte"]') || []
        ).find((input) => input.value === 'Ohne Grußkarte');

        if (baseGreeting && !baseGreeting.checked) {
          this.configureNativePicker();
          this.showMessage('Die Auswahl wird vorbereitet. Bitte anschließend erneut in den Warenkorb legen.', true);
          return;
        }

        if (!parentVariant?.id) {
          this.showMessage('Bitte zuerst einen Inhalt auswählen.', true);
          return;
        }

        if (!baseLidVariantId || this.dataset.baseLidAvailable !== 'true') {
          this.showMessage('Derzeit sind keine Geschenkdosen verfügbar.', true);
          return;
        }

        if (!label?.value) {
          this.showMessage('Bitte ein Deckelmotiv auswählen.', true);
          return;
        }

        if (availableLimit !== null && quantity > availableLimit) {
          this.showMessage(`Für diese Konfiguration sind maximal ${availableLimit} Stück verfügbar.`, true);
          return;
        }

        const groupId = `nm-gift-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const labelTitle = label.dataset.title || 'Ohne Motiv';
        const cardTitle = card?.dataset.title || 'Ohne Grußkarte';
        const selectedLabelVariantId = Number(label.value);
        const parentVariantId = Number(parentVariant.id);

        const items = [
          {
            id: parentVariantId,
            quantity,
            properties: {
              Deckelmotiv: labelTitle,
              'Grußkarte': cardTitle,
              _nm_gift_group: groupId,
              _nm_gift_role: 'Geschenkdose',
            },
          },
          {
            id: baseLidVariantId,
            quantity,
            parent_id: parentVariantId,
            properties: {
              _nm_gift_group: groupId,
              _nm_gift_role: 'Deckel',
            },
          },
        ];

        if (selectedLabelVariantId !== baseLidVariantId) {
          items.push({
            id: selectedLabelVariantId,
            quantity,
            parent_id: parentVariantId,
            properties: {
              _nm_gift_group: groupId,
              _nm_gift_role: 'Deckelaufkleber',
            },
          });
        }

        if (card?.value) {
          items.push({
            id: Number(card.value),
            quantity,
            parent_id: parentVariantId,
            properties: {
              _nm_gift_group: groupId,
              _nm_gift_role: 'Grußkarte',
            },
          });
        }

        this.setLoading(true);

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });

          const result = await response.json();

          if (!response.ok || result?.status) {
            throw new Error(result?.description || result?.message || 'Die Konfiguration konnte nicht hinzugefügt werden.');
          }

          window.location.assign(window.routes?.cart_url || '/cart');
        } catch (error) {
          console.error('Nussmart gift configurator add-to-cart error:', error);
          this.showMessage(error.message || 'Die Konfiguration konnte nicht hinzugefügt werden.', true);
          this.setLoading(false);
        }
      }
    }
  );
}
